import type { PlanFeature } from '../types/api';

/**
 * The shell's destinations, in one place for the sidebar, the phone tab bar
 * and the More sheet. Glyphs come from the vendored Lucide font
 * (`public/fonts/lucide.woff2`, the same font the slide icons use; codepoints
 * from its cmap).
 */
export interface ShellLink {
  to: string;
  label: string;
  /** NavLink `end`: only the exact path counts as here. */
  end: boolean;
  icon: number;
  /** The plan feature the destination is; the link hides on a plan without it. */
  feature?: PlanFeature;
}

/**
 * Eight links in three groups for the sidebar (and, on a tablet, the rail).
 * Settings stays in the header; Ctrl K still reaches every screen and
 * knowledge-base section.
 */
export const NAV_GROUPS: readonly { label: string; items: readonly ShellLink[] }[] = [
  {
    label: 'Create',
    items: [
      { to: '/', label: 'Home', end: true, icon: 0xe0f5 },
      { to: '/generation', label: 'Create post', end: false, icon: 0xe412 },
      { to: '/templates', label: 'Templates', end: false, icon: 0xe207 },
      { to: '/history', label: 'History', end: false, icon: 0xe1f5 },
    ],
  },
  {
    label: 'Plan',
    items: [
      { to: '/calendar', label: 'Calendar', end: false, icon: 0xe063 },
      { to: '/assistant', label: 'AI assistant', end: false, icon: 0xe5ce, feature: 'assistant' },
      { to: '/news', label: 'News', end: false, icon: 0xe348 },
    ],
  },
  {
    label: 'Brand',
    items: [{ to: '/knowledge-base', label: 'Brand profile', end: false, icon: 0xe05f }],
  },
];

const ALL_LINKS = NAV_GROUPS.flatMap((group) => group.items);

function link(to: string): ShellLink {
  const found = ALL_LINKS.find((item) => item.to === to);
  if (!found) throw new Error(`Unknown shell link: ${to}`);
  return found;
}

/** The phone tab bar's four links; the fifth slot is More. */
export const TAB_LINKS: readonly ShellLink[] = [
  link('/'),
  link('/generation'),
  link('/history'),
  link('/calendar'),
];

/** Everything else, in the More sheet. Settings moves here from the header. */
export const MORE_LINKS: readonly ShellLink[] = [
  link('/templates'),
  link('/assistant'),
  link('/news'),
  link('/knowledge-base'),
  { to: '/settings', label: 'Settings', end: false, icon: 0xe154 },
];

/** Lucide glyphs the phone and tablet shell add. */
export const GLYPH_SEARCH = String.fromCodePoint(0xe151); // search
export const GLYPH_MORE = String.fromCodePoint(0xe0b6); // ellipsis
export const GLYPH_SIGN_OUT = String.fromCodePoint(0xe10e); // log-out
export const GLYPH_LAMP = String.fromCodePoint(0xe2da); // lamp-desk
export const GLYPH_DOWNLOAD = String.fromCodePoint(0xe0b2); // download
export const GLYPH_SHARE = String.fromCodePoint(0xe155); // share, the iOS Share button's shape

/** The links a plan shows: those with no feature, and those whose feature `can` allows. */
export function linksForPlan(
  links: readonly ShellLink[],
  can: (feature: PlanFeature) => boolean,
): ShellLink[] {
  return links.filter((item) => item.feature === undefined || can(item.feature));
}

/** Whether `pathname` is at or under `to`, the way a non-`end` NavLink matches. */
export function isAtOrUnder(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}
